<script setup lang="ts">
import type { NavigationMenuItem } from '@nuxt/ui'
import { computed, ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useDashboards } from '@/stores/dashboards'
import type { Analysis } from '@/stores/dashboards'
import { useAdmin } from '@/stores/admin'
import { slugify } from '@/lib/utils'
import logoUrl from '@/assets/logo.png'
import UserMenu from '@/views/UserMenu.vue'

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const props = defineProps<{
  title: string
  id: string | undefined
}>()

const auth = useAuthStore()
const route = useRoute()
const dashboards = useDashboards()
const admin = useAdmin()

type OrganizationProps = {
  id: string
  name: string
  logo_url: string | undefined
}

const organization = ref<OrganizationProps | null>(null)
const showReportsModal = ref(false)

// Check if we're in preview mode
const isPreviewMode = computed(() => route.path.startsWith('/admin/preview/'))

const userTopSidebarLink = computed(() => {
  const homeLink = {
    label: 'Home',
    icon: 'i-lucide-house',
    to: isPreviewMode.value
      ? { name: 'admin-preview', params: { orgId: route.params.orgId } }
      : '/',
  }
  return [homeLink]
})

const adminOrganizationsLink = {
  label: 'Organizations',
  icon: 'i-lucide-building-2',
  to: '/admin',
} satisfies NavigationMenuItem

const adminBrandsLink = {
  label: 'Brands',
  icon: 'i-lucide-tag',
  to: '/admin/brands',
} satisfies NavigationMenuItem

const adminSectorsLink = {
  label: 'Sectors',
  icon: 'i-lucide-layers',
  to: '/admin/sectors',
} satisfies NavigationMenuItem

const adminAdvertisersLink = {
  label: 'Advertisers',
  icon: 'i-lucide-megaphone',
  to: '/admin/advertisers',
} satisfies NavigationMenuItem

const adminImportsLink = {
  label: 'Imports',
  icon: 'i-lucide-download',
  to: '/admin/imports',
} satisfies NavigationMenuItem

const adminTopSidebarLink = [
  adminOrganizationsLink,
  adminBrandsLink,
  adminSectorsLink,
  adminAdvertisersLink,
  adminImportsLink,
]

// Check if we're on an admin organization page
const isAdminOrgPage = computed(() => Boolean(route.meta.showOrgSidebar))

// Get the current organization being managed
const currentOrg = computed(() => admin.organizations.find((org) => org.slug === route.params.slug))

const adminOrgSidebarLinks = computed<NavigationMenuItem[]>(() => {
  if (!isAdminOrgPage.value) return []
  const slug = route.params.slug as string
  if (!slug) return []
  const org = currentOrg.value
  return [
    {
      label: slug,
      icon: org?.logo_url?.length ? undefined : 'i-lucide-circle-dashed',
      avatar: org?.logo_url?.length ? { src: org.logo_url, size: '2xs' as const } : undefined,
      defaultOpen: true,
      children: [
        {
          label: 'Overview',
          icon: 'i-lucide-info',
          to: { name: 'admin-organization', params: { slug } },
        },
        {
          label: 'Setup',
          icon: 'i-lucide-settings',
          to: { name: 'admin-organization-setup', params: { slug } },
        },
        {
          label: 'Users',
          icon: 'i-lucide-users',
          to: { name: 'admin-organization-users', params: { slug } },
        },
        {
          label: 'Content',
          icon: 'i-lucide-layout-dashboard',
          to: { name: 'admin-organization-content', params: { slug } },
        },
      ],
    },
  ]
})

const sidebarItems = computed(() => {
  // admin user
  if (auth.isAdmin) {
    // in preview mode, show analysis sidebar items
    if (isPreviewMode.value) {
      return [
        ...userTopSidebarLink.value,
        ...getAnalysisSidebarItems(
          dashboards.analysis,
          dashboards.reportsEnabled,
          true,
          route.params.orgId as string,
        ),
      ]
    }
    // on admin organization page, insert org sub-navigation after Organizations link
    if (isAdminOrgPage.value) {
      return [
        adminOrganizationsLink,
        ...adminOrgSidebarLinks.value,
        ...adminTopSidebarLink.slice(1),
      ]
    }
    return adminTopSidebarLink
  }

  // normal user
  return [
    ...userTopSidebarLink.value,
    ...getAnalysisSidebarItems(dashboards.analysis, dashboards.reportsEnabled),
  ]
})

const getAnalysisSidebarItems = (
  analyses: Analysis[],
  reportsEnabled: boolean = false,
  isPreview: boolean = false,
  organizationId: string | undefined = undefined,
): NavigationMenuItem[] => {
  const icons = [
    'i-lucide-globe',
    'i-lucide-chart-column-stacked',
    'i-lucide-book-image',
    'i-lucide-columns-3-cog',
  ]

  // Helper function to get icon for analysis
  const getIconForAnalysis = (analysis: Analysis, idx: number): string => {
    if (analysis.icon) {
      // Format icon from "lucide-house" to "i-lucide-house"
      return analysis.icon.startsWith('i-') ? analysis.icon : `i-${analysis.icon}`
    }
    // Fallback to hardcoded icons
    return icons[idx % icons.length]
  }

  // titles starting with __ are hidden pages (reachable by URL, not in the menu)
  const visible = analyses.filter((a) => !a.title.startsWith('__'))
  const analysisItems = visible.map((analysis, idx) => {
    if (analysis.dashboards.length === 1) {
      // for analyses with only one dashboard, we don't need to show the analysis title
      return {
        label: analysis.dashboards[0].title,
        to: isPreview
          ? `/admin/preview/${organizationId}/dashboard/${analysis.dashboards[0].id}`
          : `/dashboard/${analysis.dashboards[0].id}`,
        icon: getIconForAnalysis(analysis, idx),
      }
    }
    return {
      label: analysis.title,
      icon: getIconForAnalysis(analysis, idx),
      active: analysis.dashboards.some(
        (dashboard) => route.path.indexOf(`/dashboard/${dashboard.id}`) !== -1,
      ),
      defaultOpen: analysis.dashboards.some(
        (dashboard) => route.path.indexOf(`/dashboard/${dashboard.id}`) !== -1,
      ),
      children: analysis.dashboards.map((dashboard) => {
        return {
          label: dashboard.title,
          to: isPreview
            ? `/admin/preview/${organizationId}/dashboard/${dashboard.id}`
            : `/dashboard/${dashboard.id}`,
        }
      }),
    }
  })

  // Reports menu item (enterprise feature)
  const reportsItem: NavigationMenuItem = reportsEnabled
    ? {
        label: 'Reports',
        icon: 'i-lucide-file-chart-column',
        to: 'https://reports.ginjer.ai',
        target: '_blank',
      }
    : {
        label: 'Reports',
        icon: 'i-lucide-file-chart-column',
        badge: {
          label: 'ENTERPRISE',
          color: 'primary' as const,
          variant: 'subtle' as const,
        },
        class: 'reports-locked',
        onSelect: () => {
          showReportsModal.value = true
        },
      }

  return [...analysisItems, reportsItem]
}

onMounted(async () => {
  if (isPreviewMode.value) {
    const org = await admin.fetchOrganization(route.params['orgId'] as string)
    organization.value = {
      id: org.id,
      name: org.name,
      logo_url: org.logo_url || undefined,
    }
  } else if (auth.organization) {
    organization.value = {
      id: auth.organization?.organization_id,
      name: auth.organization?.organization_name,
      logo_url: auth.organization?.organization_logo_url,
    }
  }
})
</script>

<template>
  <UDashboardGroup unit="rem" storage="local" v-if="auth.isAuthenticated">
    <UDashboardSidebar
      id="default"
      collapsible
      resizable
      class="bg-elevated/25"
      :ui="{ footer: 'lg:border-t lg:border-default' }"
    >
      <template #header="{ collapsed }">
        <img v-if="collapsed" :src="logoUrl" class="h-5 w-auto ml-1" />
        <img v-else :src="logoUrl" class="h-5 w-auto ml-2 mr-1" />
        <h1 v-if="!collapsed" class="text-2xl font-bold">Ginjer AI</h1>
      </template>

      <template #default="{ collapsed }" v-if="auth.isAuthenticated">
        <UNavigationMenu
          :collapsed="collapsed"
          :items="[sidebarItems]"
          orientation="vertical"
          :external-icon="false"
          tooltip
          popover
        />
      </template>

      <template #footer="{ collapsed }">
        <UserMenu :collapsed="collapsed" />
      </template>
    </UDashboardSidebar>

    <UDashboardPanel :id="id || slugify(title)">
      <template #header>
        <UDashboardNavbar :title="title">
          <template #leading>
            <UDashboardSidebarCollapse />
          </template>
          <template #right>
            <div class="flex items-center gap-3">
              <slot name="actions"></slot>
              <template v-if="!auth.isAdmin || isPreviewMode">
                {{ organization?.name }}
                <UAvatar :src="organization?.logo_url" />
              </template>
            </div>
          </template>
        </UDashboardNavbar>
      </template>

      <template #body>
        <slot></slot>
      </template>
    </UDashboardPanel>

    <!-- Reports Enterprise Feature Modal -->
    <UModal v-model:open="showReportsModal">
      <template #content>
        <UCard>
          <template #header>
            <h3 class="text-lg font-semibold">Enterprise Feature</h3>
          </template>
          <p>Reports is an enterprise feature. Contact us to enable it for your organization.</p>
          <template #footer>
            <div class="flex gap-2">
              <UButton variant="outline" @click="showReportsModal = false">Close</UButton>
              <UButton
                color="primary"
                tag="a"
                href="mailto:support@ginjer.ai?subject=Enable Reports Feature"
              >
                Contact Support
              </UButton>
            </div>
          </template>
        </UCard>
      </template>
    </UModal>
  </UDashboardGroup>
</template>

<style>
.reports-locked {
  position: relative;
}
.reports-locked::after {
  content: 'Enterprise feature · Click to contact us';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 6px;
  padding: 6px 12px;
  background: var(--ui-bg-inverted);
  color: white;
  font-size: 11px;
  white-space: nowrap;
  border-radius: 6px;
  opacity: 0;
  pointer-events: none;
  z-index: 9999;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}
.reports-locked:hover::after {
  opacity: 1;
}
</style>
